<template>
  <main class="session-page" style="display: flex; flex-direction: column; background: #f8fafc;">
    <!-- 顶部操作栏 -->
    <div class="session-page__top-bar">
      <button type="button" @click="handleBack" class="session-page__back-btn">
        <i class="fa-solid fa-chevron-left session-page__back-icon"></i> 返回工作台
      </button>
      <div class="session-page__top-actions">
        <button type="button" @click="handleFeedback" class="session-page__btn-feedback">反馈</button>
        <button type="button" @click="onExportAllClick" class="session-page__btn-export">导出全部</button>
      </div>
    </div>

    <!-- 对话区域 -->
    <div class="session-page__conversation">
      <template v-for="(round, idx) in state.conversationRounds" :key="round.id">
        <!-- 时间戳 -->
        <div v-if="idx === 0" class="session-page__timestamp-wrap">
          <span class="session-page__timestamp">{{ formatTime(round.createdAt) }}</span>
        </div>

        <!-- 用户消息 -->
        <div class="session-page__row session-page__row--user">
          <div class="chat-user session-page__user-bubble">
            <div v-if="round.intent === 'extend'" class="session-page__intent-row">
              <i class="fa-solid fa-link session-page__intent-icon"></i>
              <span>基于已有视频修改</span>
            </div>
            <div v-if="round.imagesUsed?.length || round.videosUsed?.length || round.audiosUsed?.length" class="session-page__ref-thumbs">
              <img v-for="(img, i) in (round.imagesUsed || [])" :key="i" :src="img" class="session-page__ref-thumb" alt="" />
              <div v-for="(vid, i) in (round.videosUsed || [])" :key="'v'+i" class="session-page__ref-media">
                <video :src="vid" muted preload="metadata" class="session-page__ref-thumb"></video>
                <i class="fa-solid fa-film session-page__ref-badge"></i>
              </div>
              <div v-for="(_aud, i) in (round.audiosUsed || [])" :key="'a'+i" class="session-page__ref-media session-page__ref-media--audio">
                <i class="fa-solid fa-music"></i>
                <span>音频{{ i + 1 }}</span>
              </div>
            </div>
            <p class="session-page__prompt-text">{{ round.prompt }}</p>
            <p class="session-page__params-line">
              <i class="fa-solid fa-sliders session-page__params-icon"></i>{{ round.params.ratio }} | {{ round.params.resolution }} | {{ round.params.duration }}{{ round.watermark !== false ? ' | 水印' : '' }}
            </p>
          </div>
        </div>

        <!-- AI 响应 - 有视频 URL 即展示（即使 status 非 succeeded） -->
        <div v-if="round.videoUrl" class="session-page__row session-page__row--ai">
          <div class="chat-ai session-page__ai-card session-page__ai-card--video">
            <div class="vp" :class="{ 'vp--collapsed': !vpState[round.id]?.expanded }" :data-round="round.id">
              <div v-if="!vpState[round.id]?.loaded" class="vp__loading">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span>视频加载中</span>
              </div>
              <video
                :ref="(el: any) => { if (el) videoRefs[round.id] = el }"
                :src="round.videoUrl"
                class="vp__video"
                :class="{ 'vp__video--hidden': !vpState[round.id]?.loaded }"
                preload="metadata"
                @click="vpToggle(round.id)"
                @timeupdate="vpTimeUpdate(round.id)"
                @loadedmetadata="vpMetaLoaded(round.id)"
                @ended="vpState[round.id] && (vpState[round.id].playing = false)"
              ></video>
              <!-- 遮罩：加载完且未展开时显示 -->
              <div v-if="vpState[round.id]?.loaded && !vpState[round.id]?.expanded" class="vp__overlay" @click="vpToggle(round.id)">
                <div class="vp__play-btn"><i class="fa-solid fa-play"></i></div>
              </div>
              <div v-if="vpState[round.id]?.loaded" class="vp__controls">
                <div class="vp__progress" @click="vpSeek(round.id, $event)">
                  <div class="vp__progress-fill" :style="{ width: (vpState[round.id]?.pct || 0) + '%' }"></div>
                </div>
                <div class="vp__bar">
                  <button class="vp__btn" @click="vpToggle(round.id)">
                    <i :class="vpState[round.id]?.playing ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
                  </button>
                  <span class="vp__time">{{ vpFmt(vpState[round.id]?.cur || 0) }} / {{ vpFmt(vpState[round.id]?.dur || 0) }}</span>
                  <span class="vp__spacer"></span>
                  <button class="vp__btn" @click="vpMute(round.id)">
                    <i :class="vpState[round.id]?.muted ? 'fa-solid fa-volume-xmark' : 'fa-solid fa-volume-high'"></i>
                  </button>
                  <button class="vp__btn" @click="vpFullscreen(round.id)">
                    <i class="fa-solid fa-expand"></i>
                  </button>
                </div>
              </div>
            </div>
            <div v-if="showDowngradeNotice(round)" class="session-page__downgrade-notice session-page__downgrade-notice--warn" role="status">
              <i class="fa-solid fa-triangle-exclamation session-page__downgrade-icon" aria-hidden="true"></i>
              <span>{{ downgradeNoticeText(round) }}</span>
            </div>
            <div v-if="showCdnStatus(round)" class="session-page__cdn-status" :class="'session-page__cdn-status--' + cdnStatusKind(round)">
              <template v-if="cdnStatusKind(round) === 'uploading'">
                <i class="fa-solid fa-cloud-arrow-up session-page__cdn-icon session-page__cdn-icon--pulse" aria-hidden="true"></i>
                <span>正在转存到云端，若失败视频将在生成后24小时内失效</span>
              </template>
              <template v-else-if="cdnStatusKind(round) === 'done'">
                <i class="fa-solid fa-circle-check session-page__cdn-icon" aria-hidden="true"></i>
                <span>已保存到云端</span>
              </template>
              <template v-else>
                <i class="fa-solid fa-circle-exclamation session-page__cdn-icon" aria-hidden="true"></i>
                <span>转存失败，视频将在生成后24小时内失效，请及时下载保存</span>
              </template>
            </div>
            <div class="session-page__video-toolbar">
              <div class="session-page__toolbar-btns">
                <button type="button" @click="onSessionReEdit(round.id)" class="session-page__btn-primary">
                  <i class="fa-solid fa-wand-magic-sparkles session-page__btn-icon"></i>重新编辑
                </button>
                <button type="button" disabled class="session-page__btn-secondary session-page__btn--sunset" title="Beta 试用已结束，生成功能已暂停">
                  <i class="fa-solid fa-rotate-right session-page__btn-icon"></i>再次生成
                </button>
              </div>
              <button type="button" @click="onSessionDownload(round.id)" class="session-page__btn-icon-only">
                <i class="fa-solid fa-download session-page__icon-sm"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- AI 响应 - 失败（无 videoUrl 时才展示错误文案） -->
        <div v-else-if="round.status === 'failed' && !round.videoUrl" class="session-page__row session-page__row--ai">
          <div class="chat-ai session-page__ai-card session-page__ai-card--error">
            <p class="session-page__error-msg"><i class="fa-solid fa-circle-exclamation session-page__error-icon"></i>{{ round.statusLabel || '生成失败，请调整描述后重试' }}</p>
          </div>
        </div>

        <!-- AI 响应 - 已取消 -->
        <div v-else-if="round.status === 'cancelled'" class="session-page__row session-page__row--ai">
          <div class="chat-ai session-page__ai-card session-page__ai-card--cancelled">
            <p class="session-page__cancelled-msg"><i class="fa-solid fa-ban session-page__cancelled-icon"></i>已取消生成</p>
          </div>
        </div>

        <!-- AI 响应 - 意图引导 -->
        <div v-else-if="round.status === 'specify_target' || round.status === 'not_video'" class="session-page__row session-page__row--ai">
          <div class="chat-ai session-page__ai-card session-page__ai-card--message">
            <p class="session-page__assistant-msg">{{ round.assistantMessage }}</p>
          </div>
        </div>

        <!-- AI 响应 - 加载中 -->
        <div v-else class="session-page__row session-page__row--ai">
          <div class="chat-ai session-page__ai-card session-page__ai-card--loading">
            <div class="session-page__loading-shell">
              <div class="session-page__loading-body">
                <div class="loader-ring session-page__loader-ring"></div>
                <p class="session-page__loading-label progress-pulse">
                  {{ round.status === 'analyzing' ? '正在分析您的需求...' : (round.statusLabel || '排队中...') }}
                </p>
                <div v-if="round.status !== 'analyzing'" class="session-page__progress-track">
                  <div class="session-page__progress-fill"
                       :style="{ width: (round.progress || 5) + '%' }"></div>
                </div>
              </div>
            </div>
            <div v-if="showDowngradeNotice(round)" class="session-page__downgrade-notice session-page__downgrade-notice--warn" role="status">
              <i class="fa-solid fa-triangle-exclamation session-page__downgrade-icon" aria-hidden="true"></i>
              <span>{{ downgradeNoticeText(round) }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 对话输入框 (固定底部) — Beta 下线后整体禁用 REQ-SUNSET-002 -->
    <div class="session-page__input-bar session-page__input-bar--sunset">
      <div class="session-page__sunset-mask">试用已结束，无法发送新指令</div>
      <p v-if="isUploading" class="session-page__upload-hint">
        正在上传素材 {{ state.uploadProgress.completed }}/{{ state.uploadProgress.total }}
      </p>
      <!-- 已上传素材缩略图 -->
      <div v-if="hasUploadedMedia" class="session-page__images-row">
        <div v-for="img in state.uploadedImages" :key="img.id" class="image-thumbnail">
          <img :src="img.data" :alt="img.name" />
          <span class="remove-btn" @click="removeUploadedImage(img.id)" title="移除图片">✕</span>
        </div>
        <div v-for="vid in state.uploadedVideos" :key="vid.id" class="media-thumbnail media-thumbnail--video">
          <video :src="vid.data" muted preload="metadata"></video>
          <span class="media-thumbnail__badge"><i class="fa-solid fa-film"></i></span>
          <span class="remove-btn" @click="removeUploadedVideo(vid.id)" title="移除视频">✕</span>
        </div>
        <div v-for="aud in state.uploadedAudios" :key="aud.id" class="media-thumbnail media-thumbnail--audio" :title="aud.name">
          <i class="fa-solid fa-music media-thumbnail__audio-icon"></i>
          <span class="media-thumbnail__name">{{ aud.name }}</span>
          <span class="remove-btn" @click="removeUploadedAudio(aud.id)" title="移除音频">✕</span>
        </div>
      </div>
      <div class="session-page__input-inner-wrap">
        <div class="session-page__composer-field">
          <button type="button" @click="onSessionUploadClick" class="session-page__upload-trigger" data-tip="图片最多9张；视频最多2个；音频最多3个">
            <i class="fa-solid fa-paperclip session-page__upload-icon"></i>素材
          </button>
          <div class="session-page__mention-wrap">
            <button type="button" @mousedown.prevent @click="toggleMentionDrop" class="session-page__upload-trigger">
              <i class="fa-solid fa-at session-page__upload-icon"></i>引用
            </button>
            <div v-if="state.mentionDropOpen && (state.uploadedImages.length > 0 || state.uploadedVideos.length > 0 || state.uploadedAudios.length > 0)" class="session-page__mention-drop">
              <template v-if="state.uploadedImages.length > 0">
                <p class="session-page__mention-heading">图片</p>
                <div v-for="(img, idx) in state.uploadedImages" :key="img.id"
                     class="session-page__mention-option" @mousedown.prevent @click="doInsertMention('image', idx)">
                  <img :src="img.data" :alt="'图像' + (idx + 1)" />
                  <span>图像{{ idx + 1 }}</span>
                </div>
              </template>
              <template v-if="state.uploadedVideos.length > 0">
                <p class="session-page__mention-heading">视频</p>
                <div v-for="(vid, idx) in state.uploadedVideos" :key="vid.id"
                     class="session-page__mention-option" @mousedown.prevent @click="doInsertMention('video', idx)">
                  <i class="fa-solid fa-video"></i>
                  <span>视频{{ idx + 1 }}</span>
                </div>
              </template>
              <template v-if="state.uploadedAudios.length > 0">
                <p class="session-page__mention-heading">音频</p>
                <div v-for="(aud, idx) in state.uploadedAudios" :key="aud.id"
                     class="session-page__mention-option" @mousedown.prevent @click="doInsertMention('audio', idx)">
                  <i class="fa-solid fa-music"></i>
                  <span>音频{{ idx + 1 }}</span>
                </div>
              </template>
            </div>
          </div>
          <span class="session-page__model-badge">
            <i class="fa-solid fa-microchip"></i>{{ state.selectedModelLabel }}
          </span>
          <textarea ref="composerTextarea" v-model="followUpText" rows="1"
                    class="session-page__composer-textarea"
                    placeholder="输入追加指令，比如：把光影调亮一点..."
                    @compositionstart="isComposing = true"
                    @compositionend="isComposing = false"
                    @keydown.enter.exact="onEnterKey"
                    @keyup="saveCursorPos"
                    @mouseup="saveCursorPos"
                    @input="saveCursorPos"
                    ></textarea>
        </div>
        <button v-if="hasActiveRound" type="button" @click="doCancel" class="session-page__send-btn session-page__send-btn--stop">
          <i class="fa-solid fa-stop"></i>
        </button>
        <button v-else type="button" @click="doSendFollowUp" :disabled="isUploading" class="session-page__send-btn">
          <i :class="isUploading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-arrow-up'"></i>
        </button>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useAppStore } from '../composables/useAppStore'
import { useModal } from '../composables/useModal'
import { reportClick } from '../util/report'
import type { ConversationRound } from '../types'

const { state, showPage, reEdit, downloadVideo, cancelGeneration, sendFollowUp, toggleMentionDrop, removeUploadedImage, removeUploadedVideo, removeUploadedAudio } = useAppStore()

const isUploading = computed(() => state.uploadProgress.phase === 'uploading')

const hasUploadedMedia = computed(
  () =>
    state.uploadedImages.length > 0 ||
    state.uploadedVideos.length > 0 ||
    state.uploadedAudios.length > 0,
)

function formatTime(iso?: string): string {
  const d = iso ? new Date(iso) : new Date()
  return d.toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

type CdnStatusKind = 'uploading' | 'done' | 'failed'

function cdnStatusKind(round: ConversationRound): CdnStatusKind {
  if (round.cdnStatus === 'done') return 'done'
  if (round.cdnStatus === 'failed') return 'failed'
  return 'uploading'
}

const DEFAULT_DOWNGRADE_NOTICE = '检测到图片包含人物形象，已启用智能兼容模式'

function showDowngradeNotice(round: ConversationRound): boolean {
  const status = round.downgrade_status
  return status === 'attempted' || status === 'succeeded'
}

function downgradeNoticeText(round: ConversationRound): string {
  const text = round.downgrade_notice?.trim()
  return text || DEFAULT_DOWNGRADE_NOTICE
}

function showCdnStatus(round: ConversationRound): boolean {
  if (!round.videoUrl) return false
  if (!round.cdnStatus || round.cdnStatus === 'done') return false
  return true
}
const { toast, confirm } = useModal()

const hasActiveRound = computed(() =>
  state.conversationRounds.some(r => r.status === 'pending' || r.status === 'analyzing')
)

const followUpText = ref('')
const isComposing = ref(false)
const composerTextarea = ref<HTMLTextAreaElement | null>(null)

function onEnterKey(e: KeyboardEvent) {
  if (isComposing.value) return
  e.preventDefault()
  if (hasActiveRound.value) return
  doSendFollowUp()
}

const videoRefs: Record<string, HTMLVideoElement> = {}
const vpState = reactive<Record<string, { playing: boolean; expanded: boolean; cur: number; dur: number; pct: number; muted: boolean; loaded: boolean }>>({})

function ensureVP(id: string) {
  if (!vpState[id]) vpState[id] = { playing: false, expanded: false, cur: 0, dur: 0, pct: 0, muted: false, loaded: false }
}
function vpToggle(id: string) {
  ensureVP(id)
  const v = videoRefs[id]; if (!v) return
  if (v.paused) { v.play(); vpState[id].playing = true; vpState[id].expanded = true } else { v.pause(); vpState[id].playing = false }
}
function vpTimeUpdate(id: string) {
  ensureVP(id)
  const v = videoRefs[id]; if (!v) return
  vpState[id].cur = v.currentTime
  vpState[id].pct = v.duration ? (v.currentTime / v.duration) * 100 : 0
}
function vpMetaLoaded(id: string) {
  ensureVP(id)
  const v = videoRefs[id]; if (!v) return
  vpState[id].dur = v.duration
  vpState[id].loaded = true
}
function vpSeek(id: string, e: MouseEvent) {
  const v = videoRefs[id]; if (!v || !v.duration) return
  const bar = e.currentTarget as HTMLElement
  const pct = Math.max(0, Math.min(1, (e.clientX - bar.getBoundingClientRect().left) / bar.clientWidth))
  v.currentTime = pct * v.duration
}
function vpMute(id: string) {
  ensureVP(id)
  const v = videoRefs[id]; if (!v) return
  v.muted = !v.muted; vpState[id].muted = v.muted
}
function vpFullscreen(id: string) {
  const el = document.querySelector(`.vp[data-round="${id}"]`) as HTMLElement
  if (!el) return
  if (document.fullscreenElement) document.exitFullscreen()
  else el.requestFullscreen()
}
function vpFmt(sec: number): string {
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60)
  return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0')
}


async function handleBack() {
  reportClick('detail', 'back_home')
  if (hasActiveRound.value) {
    const yes = await confirm('当前有视频正在生成，离开将取消生成任务，确定返回吗？', {
      title: '确认离开',
      confirmText: '确认离开',
      cancelText: '继续等待',
    })
    if (!yes) return
    doCancel()
  }
  showPage('home')
}

function onBeforeUnload(e: BeforeUnloadEvent) {
  if (hasActiveRound.value) {
    e.preventDefault()
  }
}

onMounted(() => {
  window.addEventListener('beforeunload', onBeforeUnload)
})
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', onBeforeUnload)
})

function handleFeedback() {
  reportClick('detail', 'feedback')
  const product_id = 8000036
  const product_name = encodeURIComponent('AI视频')
  const app_name = 'aivideo'
  const qs = `product_id=${product_id}&product_name=${product_name}&tofeedback=true&app_name=${app_name}`
  window.open(`https://get.wps.cn/feedback/pc?${qs}`, '_blank')
}

function exportAll() {
  const videos = state.conversationRounds.filter(r => r.videoUrl)
  if (videos.length === 0) {
    toast.warning('当前会话没有已完成的视频')
    return
  }
  videos.forEach((round, i) => {
    setTimeout(() => downloadVideo(round.id, { batch: true }), i * 500)
  })
  toast.success(`正在下载 ${videos.length} 个视频`)
}

function triggerUpload() {
  document.getElementById('media-upload-input')?.click()
}

function onSessionReEdit(id: string) {
  reportClick('detail', 'edit_again')
  reEdit(id)
}

function onSessionDownload(id: string) {
  reportClick('detail', 'video_download')
  downloadVideo(id)
}

function onExportAllClick() {
  reportClick('detail', 'video_download_all')
  exportAll()
}

function onSessionUploadClick() {
  reportClick('detail', 'add_image')
  triggerUpload()
}

function saveCursorPos() {
  // keep cursor tracking for future use
}

function doInsertMention(type: 'image' | 'video' | 'audio', idx: number) {
  const labels: Record<string, string> = { image: '图像', video: '视频', audio: '音频' }
  const tag = `@${labels[type]}${idx + 1} `
  const el = composerTextarea.value
  state.mentionDropOpen = false
  if (!el) {
    followUpText.value += tag
    return
  }
  el.focus()
  const start = el.selectionStart ?? followUpText.value.length
  el.setSelectionRange(start, start)
  document.execCommand('insertText', false, tag)
  followUpText.value = el.value
}

function doSendFollowUp() {
  if (!followUpText.value.trim()) return
  reportClick('detail', 'send')
  sendFollowUp(followUpText.value.trim())
  followUpText.value = ''
}

function doCancel() {
  const active = state.conversationRounds.find(
    r => r.status === 'pending' || r.status === 'analyzing'
  )
  if (active) {
    reportClick('detail', 'stop')
    cancelGeneration(active.id)
  }
}

let initialLoaded = false

function scrollToBottom() {
  nextTick(() => {
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    }, 150)
  })
}

watch(() => state.conversationRounds.length, (newLen, oldLen) => {
  if (!initialLoaded) {
    initialLoaded = true
    scrollToBottom()
    return
  }
  if (newLen > oldLen) scrollToBottom()
})

watch(
  () => state.conversationRounds.map(r => `${r.status}|${r.videoUrl || ''}`).join(','),
  () => {
    if (initialLoaded) scrollToBottom()
  }
)
</script>

<style scoped>
.session-page {
  min-height: calc(100vh - 49px);
}

.session-page__top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 2rem;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.session-page__back-btn {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  background: transparent;
  border: none;
  cursor: pointer;
  transition:
    color 0.15s ease,
    background-color 0.15s ease;
}

.session-page__back-btn:hover {
  color: #111827;
}

.session-page__back-icon {
  margin-right: 0.5rem;
  font-size: 0.75rem;
}

.session-page__top-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.session-page__btn-feedback {
  padding: 0.375rem 1rem;
  font-size: 0.875rem;
  color: #6b7280;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.session-page__btn-feedback:hover {
  background: #f3f4f6;
}

.session-page__btn-export {
  padding: 0.375rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #ffffff;
  background: #111827;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
  transition: background-color 0.15s ease;
}

.session-page__btn-export:hover {
  background: #1f2937;
}

.session-page__conversation {
  flex: 1 1 0%;
  width: 100%;
  max-width: 64rem;
  margin-left: auto;
  margin-right: auto;
  padding: 2rem 2rem 10rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.session-page__timestamp-wrap {
  text-align: center;
}

.session-page__timestamp {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  color: #9ca3af;
  background: rgb(243 244 246 / 0.5);
  border-radius: 9999px;
}

.session-page__row {
  display: flex;
}

.session-page__row--user {
  justify-content: flex-end;
}

.session-page__row--ai {
  justify-content: flex-start;
}

.session-page__user-bubble {
  max-width: 70%;
  padding: 1rem 1.25rem;
}

.session-page__intent-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 11px;
  color: rgb(59 130 246 / 0.8);
}

.session-page__intent-icon {
  margin-right: 0.375rem;
}

.session-page__ref-thumbs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.session-page__ref-thumb {
  width: 4rem;
  height: 4rem;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 1px solid #bfdbfe;
}

.session-page__ref-media {
  position: relative;
  width: 4rem;
  height: 4rem;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #bfdbfe;
}

.session-page__ref-media--audio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: #f0f4ff;
  font-size: 10px;
  color: #6366f1;
  padding: 4px;
}

.session-page__ref-media--audio i {
  font-size: 16px;
}

.session-page__ref-badge {
  position: absolute;
  left: 3px;
  bottom: 3px;
  font-size: 9px;
  background: rgba(0,0,0,0.55);
  color: #fff;
  padding: 2px 4px;
  border-radius: 3px;
}

.session-page__prompt-text {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.625;
}

.session-page__params-line {
  margin: 0.5rem 0 0;
  font-size: 11px;
  color: #9ca3af;
}

.session-page__params-icon {
  margin-right: 0.25rem;
}

.session-page__ai-card--video {
  overflow: hidden;
  max-width: 60%;
  width: 100%;
}

/* ── Inline Video Player ── */
.vp {
  position: relative;
  background: #f3f4f6;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  overflow: hidden;
}
.vp__badge {
  position: absolute; top: 12px; left: 12px;
  background: rgba(0,0,0,0.55); color: #fff;
  font-size: 11px; font-weight: 600;
  padding: 3px 8px; border-radius: 4px;
  z-index: 3; pointer-events: none;
  backdrop-filter: blur(4px);
}
.vp--collapsed {
  max-height: 400px;
}
.vp--collapsed .vp__video {
  max-height: 400px;
  object-fit: cover;
}
.vp--collapsed .vp__controls {
  display: none;
}
.vp__video {
  display: block; width: 100%; cursor: pointer;
}
.vp__video--hidden {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.vp__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 280px;
  background: #f3f4f6;
  border-radius: 12px;
  color: #9ca3af;
  font-size: 14px;
}
.vp__loading i {
  font-size: 24px;
}
.vp__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 4;
  transition: background 0.2s;
}
.vp__overlay:hover {
  background: rgba(0, 0, 0, 0.25);
}
.vp__play-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #1a1a1a;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s, box-shadow 0.2s;
}
.vp__overlay:hover .vp__play-btn {
  transform: scale(1.08);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
}
.vp__controls {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  padding: 18px 12px 10px; z-index: 2;
}
.vp__progress {
  width: 100%; height: 4px; background: rgba(255,255,255,0.25);
  border-radius: 2px; cursor: pointer; margin-bottom: 8px;
  transition: height 0.15s;
}
.vp__progress:hover { height: 6px; }
.vp__progress-fill {
  height: 100%; background: #fff; border-radius: 2px;
  transition: width 0.1s linear;
}
.vp__bar {
  display: flex; align-items: center; gap: 10px;
}
.vp__btn {
  background: none; border: none; color: #fff;
  font-size: 14px; cursor: pointer; padding: 2px 4px;
  opacity: 0.9; transition: opacity 0.15s;
}
.vp__btn:hover { opacity: 1; }
.vp__time {
  font-size: 12px; color: rgba(255,255,255,0.85);
  font-variant-numeric: tabular-nums; font-family: 'Inter', monospace;
}
.vp__spacer { flex: 1; }

/* fullscreen tweaks */
.vp:fullscreen .vp__video { height: 100vh; object-fit: contain; }
.vp:fullscreen .vp__controls { padding-bottom: 20px; }

.session-page__downgrade-notice {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  line-height: 1.5;
}

.session-page__downgrade-notice span {
  flex: 1;
  min-width: 0;
}

.session-page__downgrade-notice--warn {
  color: #92400e;
  background: #fffbeb;
}

.session-page__downgrade-icon {
  flex-shrink: 0;
  font-size: 13px;
}

.session-page__downgrade-notice--warn .session-page__downgrade-icon {
  color: #d97706;
}

.session-page__cdn-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  border-radius: 0 0 8px 8px;
  background: #f9fafb;
}

.session-page__cdn-status span {
  flex: 1;
  min-width: 0;
}

.session-page__cdn-status--uploading {
  color: #92400e;
  background: #fffbeb;
}

.session-page__cdn-status--done {
  color: #166534;
  background: #f0fdf4;
}

.session-page__cdn-status--failed {
  color: #991b1b;
  background: #fef2f2;
}

.session-page__cdn-icon {
  flex-shrink: 0;
  font-size: 14px;
}

.session-page__cdn-status--uploading .session-page__cdn-icon {
  color: #d97706;
}

.session-page__cdn-status--done .session-page__cdn-icon {
  color: #16a34a;
}

.session-page__cdn-status--failed .session-page__cdn-icon {
  color: #dc2626;
}

.session-page__cdn-icon--pulse {
  animation: session-page-cdn-pulse 1.5s ease-in-out infinite;
}

@keyframes session-page-cdn-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

.session-page__video-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-top: 1px solid #f9fafb;
}

.session-page__toolbar-btns {
  display: flex;
  gap: 0.5rem;
}

.session-page__btn-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #ffffff;
  background: #111827;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition:
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.session-page__btn-primary:hover {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.session-page__btn-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.session-page__btn-secondary:hover {
  background: #e5e7eb;
}

.session-page__btn-icon {
  margin-right: 0.25rem;
}

.session-page__btn-icon-only {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  color: #374151;
  background: #f3f4f6;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.session-page__btn-icon-only:hover {
  background: #e5e7eb;
}

.session-page__icon-sm {
  font-size: 0.875rem;
}

.session-page__ai-card--message {
  max-width: 80%;
  padding: 1rem 1.25rem;
}

.session-page__assistant-msg {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.625;
  color: #374151;
}

.session-page__ai-card--error {
  max-width: 80%;
  padding: 1.25rem;
}

.session-page__error-msg {
  margin: 0;
  font-size: 0.875rem;
  color: #ef4444;
}

.session-page__error-icon {
  margin-right: 0.5rem;
}

.session-page__ai-card--loading {
  max-width: 80%;
  width: 100%;
  padding: 1.5rem;
}

.session-page__loading-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 16 / 9;
  margin-bottom: 1rem;
  overflow: hidden;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 1rem;
}

.session-page__loading-body {
  text-align: center;
}

.session-page__loader-ring {
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1rem;
}

.session-page__loading-label {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.session-page__progress-track {
  width: 12rem;
  height: 0.375rem;
  margin: 0.75rem auto 0;
  overflow: hidden;
  background: #e5e7eb;
  border-radius: 9999px;
}

.session-page__progress-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 9999px;
  transition:
    width 0.7s ease,
    background-color 0.15s ease;
}

.session-page__loading-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.session-page__btn-disabled {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #9ca3af;
  cursor: not-allowed;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 0.75rem;
}

.session-page__btn-cancel {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
}
.session-page__btn-cancel:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.session-page__ai-card--cancelled {
  padding: 1.25rem 1.5rem;
}
.session-page__cancelled-msg {
  font-size: 0.875rem;
  color: #9ca3af;
}
.session-page__cancelled-icon {
  margin-right: 0.5rem;
  color: #d1d5db;
}

.session-page__btn-icon-only--disabled {
  color: #9ca3af;
  cursor: not-allowed;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
}

.session-page__btn-icon-only--disabled:hover {
  background: #f9fafb;
}

.session-page__upload-hint {
  max-width: 900px;
  margin: 0 auto 0.5rem;
  font-size: 0.8125rem;
  color: #6b7280;
  text-align: center;
}

.session-page__input-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  padding: 1rem;
  background: rgb(255 255 255 / 0.9);
  backdrop-filter: blur(12px);
  border-top: 1px solid #e5e7eb;
}

.session-page__input-inner-wrap {
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.session-page__composer-field {
  display: flex;
  flex: 1 1 0%;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.session-page__composer-field:focus-within {
  background: #ffffff;
  border-color: #60a5fa;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
}

.session-page__upload-trigger {
  display: flex;
  align-items: center;
  padding-right: 0.75rem;
  margin-right: 0.75rem;
  font-size: 0.75rem;
  position: relative;
  color: #6b7280;
  background: transparent;
  border: none;
  border-right: 1px solid #e5e7eb;
  cursor: pointer;
  transition: color 0.15s ease;
}

.session-page__upload-trigger:hover {
  color: #2563eb;
}

.session-page__upload-trigger[data-tip]:hover::after {
  content: attr(data-tip);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.75);
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

.session-page__upload-trigger--active {
  color: #2563eb;
}

.session-page__upload-icon {
  margin-right: 0.375rem;
}

.session-page__mention-wrap {
  position: relative;
}

.session-page__mention-drop {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border: 1px solid #f0f0f0;
  padding: 8px 0;
  min-width: 160px;
  z-index: 60;
}

.session-page__mention-heading {
  font-size: 11px;
  color: #9ca3af;
  padding: 4px 12px 6px;
  font-weight: 600;
}

.session-page__mention-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  transition: background 0.15s;
}

.session-page__mention-option:hover {
  background: #f5f5f5;
}

.session-page__mention-option img {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: cover;
}

.session-page__images-row {
  display: flex;
  gap: 0.75rem;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  padding: 8px 0 8px;
}

.session-page__model-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 11.5px;
  font-weight: 600;
  color: #6366f1;
  background: #eef2ff;
  border-radius: 999px;
  white-space: nowrap;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.session-page__composer-textarea {
  flex: 1 1 0%;
  min-height: 0;
  max-height: 100px;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #1f2937;
  resize: none;
  background: transparent;
  border: none;
  outline: none;
  overflow-y: auto;
}

.session-page__composer-textarea::-webkit-scrollbar {
  width: 4px;
}

.session-page__composer-textarea::-webkit-scrollbar-track {
  background: transparent;
}

.session-page__composer-textarea::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

.session-page__composer-textarea::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.25);
}

.session-page__send-btn {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  color: #ffffff;
  cursor: pointer;
  background: #111827;
  border: none;
  border-radius: 9999px;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;
}

.session-page__send-btn:hover {
  background: #1f2937;
}

.session-page__send-btn:active {
  transform: scale(0.95);
}

.session-page__send-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.session-page__send-btn--stop {
  background: #ef4444;
}
.session-page__send-btn--stop:hover {
  background: #dc2626;
}
.session-page__send-btn--stop i {
  font-size: 0.75rem;
}

/* ===== Beta 下线禁用 — REQ-SUNSET-002 ===== */
.session-page__input-bar--sunset {
  position: relative;
}

.session-page__sunset-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(249, 250, 251, 0.88);
  color: #9ca3af;
  font-size: 13px;
  font-weight: 500;
  border-radius: 16px;
  pointer-events: none;
  backdrop-filter: blur(1px);
  z-index: 5;
}

.session-page__btn--sunset {
  opacity: 0.45;
  cursor: not-allowed !important;
  pointer-events: none;
}
</style>
